= <template>
    <div class="EntityItemForm">
        <div class="Form">
            <div
                v-for="({ headerName, props, children }) in normalizeGroups"
                :key="headerName"
                class="mb-2"
            >
                <WText
                    tag="label"
                    typo="label"
                    weight="semi"
                    color="accent-blue"
                    size="14"
                    uppercase
                    class="Title"
                >
                    {{ headerName }}
                </WText>

                <template v-if="children">
                    <div class="Group flex flex-wrap mb-2">
                        <div
                            v-for="({ headerName: label, field, options, type, multiple, editable, required }) in children"
                            :key="field"
                            class="Field"
                        >
                            <WText
                                tag="label"
                                typo="label"
                                :size="14"
                            >
                                {{ label }} {{ required ? '*' : '' }}
                            </WText>
                            <template v-if="fieldInputs[type] === inputs.BOOL">
                                <WSwitch
                                    :value="value[field] || ''"
                                    :name="field"
                                    :disabled="disabled || editable === false || loading"
                                    color="accent-blue"
                                    @input="handleInput(key, $event)"
                                />
                            </template>
                            <template v-else-if="fieldInputs[type] === inputs.DATE">
                                <WDatepicker
                                    :value="value[field]|date"
                                    :name="field"
                                    :readonly="disabled || editable === false || loading"
                                    :placeholder="!disabled ? $t('entities.row.date.placeholder') : ''"
                                    @input="handleInput(field, $event)"
                                    @onShowPopover="$emit('onShowPopover')"
                                />
                            </template>
                            <template v-else-if="fieldInputs[type] === inputs.SELECT">
                                <WSelect
                                    :value="value[field] || ''"
                                    :name="field"
                                    :placeholder="!disabled ? $t('entities.row.select.placeholder') : ''"
                                    :options="options"
                                    :multiple="multiple"
                                    :disabled="!(options || []).length || disabled || editable === false || loading"
                                    @input="handleInput(field, $event)"
                                />
                            </template>
                            <template v-else-if="fieldInputs[type]">
                                <WInput
                                    :value="value[field] || ''"
                                    :disabled="disabled || editable === false || loading"
                                    :type="fieldInputs[type]"
                                    :name="field"
                                    :placeholder="!disabled ? $t('entities.row.text.placeholder') : ''"
                                    @input="handleInput(field, $event)"
                                />
                            </template>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { INPUTS, FIELD_INPUTS } from '@/entities/fields'

const HEADER_STYLE = {
    color: 'gray02',
    weight: 'semi',
    uppercase: true,
    size: 14,
}

const generateOptions = (options, value) => {
    if (options) return Object.keys(options).map(key => ({ key, label: options[key] }))
    if (value) return [{ key: value, label: value }] // Fallback options
    return []
}

export default {
    name: 'EntityItemForm',
    filters: {
        date (value) {
            if (!value) return undefined
            return new Date(value)
        },
    },
    props: {
        value: VueTypes.any.def({}),
        columns: VueTypes.array,
        selectGroups: VueTypes.any,
        loading: VueTypes.bool.def(false),
        disabled: VueTypes.bool.def(false),
    },
    computed: {
        normalizeGroups () {
            const { selectGroups = {}, columns = [], value } = this
            const discardFields = ['id']

            return columns.map(({ children, ...props }) => ({
                ...props,
                ...(children ? {
                    props: HEADER_STYLE,
                    children: children
                        .filter(({ field }) => !discardFields.includes(field))
                        .map(item => ({
                            ...item,
                            ...(FIELD_INPUTS[item.type] === INPUTS.SELECT
                                ? { options: generateOptions(selectGroups[item.selectGroup], value[item.field]), multiple: item.multiple }
                                : {}),
                        })),
                } : {}),
            }))
        },
    },
    beforeCreate () {
        this.inputs = INPUTS
        this.fieldInputs = FIELD_INPUTS
    },
    methods: {
        handleInput (key, value) {
            this.$emit('input', { ...this.value, [key]: value })
        },
    },
}
</script>
<style lang="scss" scoped>
.Group {
    margin: -20px 0 0 -20px;
}

.Field {
    width: calc(33.33% - 25px);
    margin: 20px 0 0 25px;
    padding: 8px 0;

    @media screen and (min-width: $xxl) {
        width: calc(25% - 20px);
    }

    @media screen and (min-width: $xxxl) {
        width: calc(20% - 20px);
    }

    &::v-deep input {
        margin-bottom: 0px !important;
    }
}

.WText.Title {
    display: block;
    margin-bottom: 5px !important;
    padding: 5px;
    background: $gray-06;
    border-radius: $border-radius;
}
</style>
